import { useRef, type ChangeEvent } from 'react'
import { Link, Upload, X, Palette, Shield, Maximize, Frame, Grid3X3, Eye } from 'lucide-react'
import type { QRSettings, DotType, CornerSquareType, CornerDotType } from '../../types/qr'
import './QRControls.scss'

interface QRControlsProps {
  settings: QRSettings
  onSettingsChange: (settings: QRSettings) => void
}

const DOT_TYPES: { value: DotType; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dots', label: 'Dots' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
]

const CORNER_SQUARE_TYPES: { value: CornerSquareType; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
  { value: 'extra-rounded', label: 'Rounded' },
]

const CORNER_DOT_TYPES: { value: CornerDotType; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
]

export function QRControls({ settings, onSettingsChange }: QRControlsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateSetting = <K extends keyof QRSettings>(
    key: K,
    value: QRSettings[K]
  ) => {
    onSettingsChange({ ...settings, [key]: value })
  }

  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const result = event.target?.result as string
      updateSetting('logoImage', result)
    }
    reader.readAsDataURL(file)
  }

  const removeLogo = () => {
    updateSetting('logoImage', undefined)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="qr-controls">
      <section className="control-section">
        <h3>
          <Link size={16} />
          URL
        </h3>
        <input
          type="url"
          className="text-input"
          placeholder="https://example.com"
          value={settings.value}
          onChange={(e) => updateSetting('value', e.target.value)}
        />
      </section>

      <section className="control-section">
        <h3>
          <Palette size={16} />
          Colors
        </h3>
        <div className="color-row">
          <label>
            <span>Foreground</span>
            <div className="color-input-wrapper">
              <input
                type="color"
                value={settings.fgColor}
                onChange={(e) => updateSetting('fgColor', e.target.value)}
              />
              <input
                type="text"
                value={settings.fgColor}
                onChange={(e) => updateSetting('fgColor', e.target.value)}
                className="color-text"
              />
            </div>
          </label>
          <label>
            <span>Background</span>
            <div className="color-input-wrapper">
              <input
                type="color"
                value={settings.bgColor}
                onChange={(e) => updateSetting('bgColor', e.target.value)}
              />
              <input
                type="text"
                value={settings.bgColor}
                onChange={(e) => updateSetting('bgColor', e.target.value)}
                className="color-text"
              />
            </div>
          </label>
        </div>
      </section>

      <section className="control-section">
        <h3>
          <Grid3X3 size={16} />
          Dot Style
        </h3>
        <div className="style-grid">
          {DOT_TYPES.map((type) => (
            <button
              key={type.value}
              className={`style-btn ${settings.dotType === type.value ? 'active' : ''}`}
              onClick={() => updateSetting('dotType', type.value)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </section>

      <section className="control-section">
        <h3>
          <Eye size={16} />
          Corner Style (Eyes)
        </h3>
        <div className="corner-controls">
          <label>
            <span>Outer Square</span>
            <div className="style-row">
              {CORNER_SQUARE_TYPES.map((type) => (
                <button
                  key={type.value}
                  className={`style-btn small ${settings.cornerSquareType === type.value ? 'active' : ''}`}
                  onClick={() => updateSetting('cornerSquareType', type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </label>
          <label>
            <span>Inner Dot</span>
            <div className="style-row">
              {CORNER_DOT_TYPES.map((type) => (
                <button
                  key={type.value}
                  className={`style-btn small ${settings.cornerDotType === type.value ? 'active' : ''}`}
                  onClick={() => updateSetting('cornerDotType', type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </label>
        </div>
      </section>

      <section className="control-section">
        <h3>
          <Maximize size={16} />
          Size
        </h3>
        <div className="slider-row">
          <input
            type="range"
            min="128"
            max="512"
            step="8"
            value={settings.size}
            onChange={(e) => updateSetting('size', Number(e.target.value))}
          />
          <span className="slider-value">{settings.size}px</span>
        </div>
      </section>

      <section className="control-section">
        <h3>Margin</h3>
        <div className="slider-row">
          <input
            type="range"
            min="0"
            max="50"
            value={settings.margin}
            onChange={(e) => updateSetting('margin', Number(e.target.value))}
          />
          <span className="slider-value">{settings.margin}px</span>
        </div>
      </section>

      <section className="control-section">
        <h3>
          <Frame size={16} />
          Border
        </h3>
        <div className="border-controls">
          <label>
            <span>Width</span>
            <div className="slider-row">
              <input
                type="range"
                min="0"
                max="20"
                value={settings.borderWidth}
                onChange={(e) => updateSetting('borderWidth', Number(e.target.value))}
              />
              <span className="slider-value">{settings.borderWidth}px</span>
            </div>
          </label>
          <label>
            <span>Radius</span>
            <div className="slider-row">
              <input
                type="range"
                min="0"
                max="50"
                value={settings.borderRadius}
                onChange={(e) => updateSetting('borderRadius', Number(e.target.value))}
              />
              <span className="slider-value">{settings.borderRadius}px</span>
            </div>
          </label>
          <label>
            <span>Color</span>
            <div className="color-input-wrapper">
              <input
                type="color"
                value={settings.borderColor}
                onChange={(e) => updateSetting('borderColor', e.target.value)}
              />
              <input
                type="text"
                value={settings.borderColor}
                onChange={(e) => updateSetting('borderColor', e.target.value)}
                className="color-text"
              />
            </div>
          </label>
        </div>
      </section>

      <section className="control-section">
        <h3>
          <Shield size={16} />
          Error Correction
        </h3>
        <div className="ec-options">
          {(['L', 'M', 'Q', 'H'] as const).map((level) => (
            <button
              key={level}
              className={`ec-btn ${settings.ecLevel === level ? 'active' : ''}`}
              onClick={() => updateSetting('ecLevel', level)}
              title={
                level === 'L' ? 'Low (~7%)' :
                level === 'M' ? 'Medium (~15%)' :
                level === 'Q' ? 'Quartile (~25%)' :
                'High (~30%)'
              }
            >
              {level}
            </button>
          ))}
        </div>
        <p className="hint">Higher = better logo tolerance, larger QR</p>
      </section>

      <section className="control-section">
        <h3>
          <Upload size={16} />
          Logo (Optional)
        </h3>
        {settings.logoImage ? (
          <div className="logo-preview">
            <img src={settings.logoImage} alt="Logo" />
            <button className="remove-logo" onClick={removeLogo}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <label className="upload-btn">
            <Upload size={18} />
            Upload Logo
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              hidden
            />
          </label>
        )}
        {settings.logoImage && (
          <div className="logo-size-controls">
            <label>
              <span>Logo Size</span>
              <div className="slider-row">
                <input
                  type="range"
                  min="20"
                  max="80"
                  value={settings.logoSize}
                  onChange={(e) => updateSetting('logoSize', Number(e.target.value))}
                />
                <span className="slider-value">{settings.logoSize}%</span>
              </div>
            </label>
          </div>
        )}
      </section>
    </div>
  )
}
